// ads-templates/common/AdsTemplateDialog.tsx
import { useState } from "react";
import { Button, Card, Dialog, DialogTitle, DialogContent, DialogActions, Box, Grid, TextField, FormGroup, FormControlLabel, Switch, IconButton, Tooltip } from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { AdsPlatformAdapter, AccountCampaign, shouldShowReportNote } from "../adapters/types";
import { DraggableChips } from "../../../../components/DraggableChips";
import { StyledToggleButton } from "../../../../components/StyledToggleButton";
import { useUser } from "../../../../Auth";
import NotificationPopup from "../../../../common/NotificationPopup";

export default function AdsCreateTemplate({ adapter, template, isCopy = false }:{
  adapter: AdsPlatformAdapter;
  template?: any;
  isCopy?: boolean; // 新增参数
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState<string>(template?.name ?? "");
  const [reportNames, setReportNames] = useState<string[]>(template?.reportNames ?? []);
  const [metricsFields, setMetricsFields] = useState<string[]>(template?.metricsFields ?? []);
  const [removeUnexposed, setRemoveUnexposed] = useState<boolean>(template?.removeUnexposed ?? true);
  const [selectedAccountCampaignList, setSelectedAccountCampaignList] = useState<AccountCampaign[]>(template?.accountCampaigns ?? []);
  const [extra, setExtra] = useState<any>(adapter.getExtraFromTemplate ? adapter.getExtraFromTemplate(template) : (template?.adTypes ?? template?.extra ?? undefined));
  const [visibleCategories, setVisibleCategories] = useState<string[]>([]);
  const user = useUser();
  
  const toggleCategoryVisibility = (category: string) => {
    setVisibleCategories(prevState =>
      prevState.includes(category)
        ? prevState.filter(cat => cat !== category)
        : [...prevState, category]
    );
  };

  const handleDeleteItem = (indexToDelete: number) => {
    setSelectedAccountCampaignList(prevList =>
      prevList.filter((_, index) => index !== indexToDelete)
    );
  };

  const [createTemplate] = adapter.useCreateTemplateMutation();
  const [updateTemplate] = adapter.useUpdateTemplateMutation();

  const resetFromTemplate = () => {
    setName(template?.name ?? "");
    setReportNames(template?.reportNames ?? []);
    setMetricsFields(template?.metricsFields ?? []);
    setRemoveUnexposed(template?.removeUnexposed ?? true);
    setSelectedAccountCampaignList(template?.accountCampaigns ?? []);
    setExtra(adapter.getExtraFromTemplate ? adapter.getExtraFromTemplate(template) : (template?.adTypes ?? template?.extra ?? undefined));
    setVisibleCategories([]);
  };

  const onCreate = async () => {
    // Add validation checks
    if (!name) {
      NotificationPopup.error(`請輸入報表名稱`);
      return;
    }
    if (!selectedAccountCampaignList.length) {
      NotificationPopup.error(`請選擇廣告帳戶或廣告活動`);
      return;
    }
    if (!reportNames.length) {
      NotificationPopup.error(`請選擇報表類型`);
      return;
    }
    if (!metricsFields.length) {
      NotificationPopup.error(`請選擇數據類型`);
      return;
    }

    const input = adapter.buildTemplateInput({
      name, 
      reportNames, 
      metricsFields, 
      accountCampaigns: selectedAccountCampaignList,
      removeUnexposed, 
      extra,
      assigneeId: user?.id
    });

    try {
      // 如果是复制模式或没有 template，调用 createTemplate
      if (!template || isCopy) {
        await createTemplate({ 
          variables: { 
            [adapter.templateInputKey]: input 
          }, 
          refetchQueries: [adapter.refetchTemplatesQueryName] 
        });
        NotificationPopup.success(isCopy ? "複製報表模板成功" : "新增報表模板成功");
      } else {
        await updateTemplate({ 
          variables: { 
            id: template.id, 
            [adapter.templateInputKey]: input 
          }, 
          refetchQueries: [adapter.refetchTemplatesQueryName] 
        });
        NotificationPopup.success("更新報表模板成功");
      }
      setOpen(false);
    } catch (error: any) {
      NotificationPopup.error(`操作失敗: ${error.message}`);
      console.error('Template operation error:', error);
    }
  };

  const AccountSelect = adapter.AccountSelect;
  const CampaignSelect = adapter.CampaignSelect;
  const MarkupButton = adapter.MarkupButton;

  return (
    <div>
      <Dialog open={open} fullWidth maxWidth="lg">
        <DialogTitle>建立{adapter.displayName}報表模板</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} direction="column">
            <Grid item>
              <TextField fullWidth label={adapter.nameLabel} value={name} onChange={e=>setName(e.target.value)} variant="outlined" />
            </Grid>

            {/* Account/Campaign Selection */}
            <Grid item container spacing={2}>
              <Grid item><AccountSelect onSelectAccount={(acc:any)=>{
                if (!selectedAccountCampaignList.some(i=>i.accountId===acc.id))
                  setSelectedAccountCampaignList(prev=>[...prev, { accountId: acc.id, accountName: acc.name }]);
              }}/></Grid>
              <Grid item><CampaignSelect onSelectCampaign={(c:any)=>{
                if (!selectedAccountCampaignList.some(i=>i.campaignId===c.id))
                  setSelectedAccountCampaignList(prev=>[...prev, { accountId: c.accountId, accountName: '', campaignId: c.id, campaignName: c.name }]);
              }}/></Grid>
              {MarkupButton ? <Grid item><MarkupButton/></Grid> : null}
            </Grid>

            {/* Display Selected Accounts/Campaigns */}
            <Grid item container spacing={1} alignItems="center">
              {selectedAccountCampaignList.map((item, index) => (
                <Grid item key={index}>
                  <Card>
                    <Box margin={1} display="flex" alignItems="center" justifyContent="space-between">
                      <span>
                        {item.accountId}-{item.accountName} 
                        {item.campaignId ? `/${item.campaignId}-${item.campaignName}` : ''}
                      </span>
                      <IconButton onClick={() => handleDeleteItem(index)} size="small">
                        <CloseIcon />
                      </IconButton>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* 平台額外選項（例如 Google 的 AdTypes） */}
            {adapter.extraOptionsRender ? (
              <>
                <Grid item>
                  廣告類型 (可複選)
                </Grid>
                <Grid item>
                  <adapter.extraOptionsRender value={extra} onChange={setExtra}/>
                </Grid>
              </>
            ) : null}

            {/* 報表類型 */}
            <Grid item>報表類型 (可複選)</Grid>
            <Grid item>
              <Card><Box minHeight={40}><DraggableChips items={reportNames} setItems={setReportNames} formatField={k=>k.split('-').map(adapter.formatField).join('-')}/></Box></Card>
            </Grid>
            <Grid item container spacing={1}>
              {adapter.reportList.map(r => (
                <Grid item key={r.name}>
                  <Card>
                    <Box m={1}>
                      <div>
                        {r.title}
                        {r.badge ? (
                          <span style={{
                            marginLeft: 6,
                            fontSize: 10,
                            background: '#0d7d5c',
                            color: 'white',
                            padding: '1px 6px',
                            borderRadius: 4,
                            fontWeight: 700,
                          }}>{r.badge}</span>
                        ) : null}
                        {shouldShowReportNote(r, extra) ? (
                          <Tooltip title={r.note ?? ''} placement="top">
                            <span style={{
                              marginLeft: 6,
                              color: '#e6a23c',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              cursor: 'help',
                              fontSize: 13,
                            }}>註</span>
                          </Tooltip>
                        ) : null}
                      </div>
                      {r.fields.map(field => {
                        const fieldName = `${r.name}-${field}`;
                        const selected = reportNames.includes(fieldName);
                        return (
                          <div key={fieldName}>
                            <StyledToggleButton
                              selected={selected}
                              value="check"
                              onChange={() => {
                                setReportNames(cur => selected ? cur.filter(n=>n!==fieldName) : [...cur, fieldName]);
                              }}
                            >
                              {field.split('-').map(adapter.formatField).join('-')}
                            </StyledToggleButton>
                          </div>
                        );
                      })}
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* 數據類型 */}
            <Grid item>數據類型 (可複選)</Grid>
            
            {/* Add category selection */}
            <Grid container spacing={1}>
              {adapter.metricsGroups.map(g => (
                <Grid item key={g.title}>
                  <StyledToggleButton
                    selected={visibleCategories.includes(g.title)}
                    value="check"
                    style={{ width: 'auto' }} 
                    onChange={() => toggleCategoryVisibility(g.title)}
                  >
                    {g.title}
                  </StyledToggleButton>
                </Grid>
              ))}
            </Grid>
            
            
            {/* 移除未曝光 */}
            <Grid item>
              <FormGroup>
                <FormControlLabel
                  control={<Switch checked={removeUnexposed} onChange={()=>setRemoveUnexposed(v=>!v)} />}
                  label={adapter.removeUnexposedLabel}
                />
              </FormGroup>
            </Grid>
            <Grid item>
              <Card>
                <Box minHeight={40}>
                  <DraggableChips items={metricsFields} setItems={setMetricsFields} formatField={adapter.formatField}/>
                </Box>
              </Card>
            </Grid>
            
            {/* Only show metrics for visible categories */}
            <Grid item container spacing={1}>
              {visibleCategories.map(category => {
                const group = adapter.metricsGroups.find(g => g.title === category);
                if (!group) return null;
                
                return (
                  <Grid item key={category}>
                    <Card>
                      <Box m={1}>
                        <div>{group.title}</div>
                        {group.fields.map(({field,label})=>{
                          const selected = metricsFields.includes(field);
                          return (
                            <div key={field}>
                              <StyledToggleButton
                                selected={selected}
                                value="check"
                                onChange={() => {
                                  setMetricsFields(cur => selected ? cur.filter(f=>f!==field) : [...cur, field]);
                                }}
                              >
                                {label}
                              </StyledToggleButton>
                            </div>
                          );
                        })}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>

            

            
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button color="secondary" onClick={()=>setOpen(false)}>取消</Button>
          <Button color="secondary" onClick={onCreate}>儲存</Button>
        </DialogActions>
      </Dialog>

      <Button
        variant="contained"
        color="primary"
        onClick={() => {
          resetFromTemplate();
          setOpen(true);
        }}
      >
        {isCopy ? '複製' : template ? '編輯' : adapter.createButtonText}
      </Button>
    </div>
  );
}
